import { useState, useEffect } from "react";
import {
  getProfile,
  updateProfile,
  getApiKeys,
  rotateApiKeys,
  getWebhookConfig,
  updateWebhookConfig,
  rotateWebhookSecret,
  testWebhook,
  getSecuritySettings,
  updateSecuritySettings,
} from "../../../services/companyAdminService";
import { useDispatch } from "react-redux";
import { useApp } from "../../../context/AppContext";
import { companyAdminApi } from "../../../store/companyAdminApi";
import { Help, ArrowForward, Science } from "@mui/icons-material";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  InputAdornment,
  Stack,
  Divider,
} from "@/components/ui";
import AppLoading from "@/components/AppLoading";

const CompanySettings = () => {
  const dispatch = useDispatch();
  const { t, isRTL, setCompanyLogoUrl, setCompanyNameFromServer } = useApp();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [profile, setProfile] = useState(null);
  const [apiKeys, setApiKeys] = useState(null);
  const [newProductionSecretKey, setNewProductionSecretKey] = useState(null);
  const [newSandboxSecretKey, setNewSandboxSecretKey] = useState(null);
  const [webhook, setWebhook] = useState(null);
  const [newWebhookSecret, setNewWebhookSecret] = useState(null);
  const [security, setSecurity] = useState(null);
  const tabKeys = ["profile", "api", "webhook", "security"];

  useEffect(() => {
    loadData();
  }, [activeTab]);

  useEffect(() => {
    if (message) setSnackbarOpen(true);
  }, [message]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (tabKeys[activeTab]) {
        case "profile":
          setProfile(await getProfile());
          break;
        case "api":
          setApiKeys(await getApiKeys());
          break;
        case "webhook":
          setWebhook(await getWebhookConfig());
          break;
        case "security":
          setSecurity(await getSecuritySettings());
          break;
      }
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRotateApiKeys = async (type) => {
    if (!window.confirm(t("rotateKeysConfirm"))) return;
    setLoading(true);
    try {
      const result = await rotateApiKeys(type);
      if (type === "production") {
        setNewProductionSecretKey(result.secretKey);
        setNewSandboxSecretKey(null);
      } else {
        setNewSandboxSecretKey(result.secretKey);
        setNewProductionSecretKey(null);
      }
      setMessage({ type: "success", text: t("keysRotatedSuccess") });
      loadData();
    } catch (error) {
      setMessage({ type: "error", text: t("keysRotateFailed") });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWebhook = async () => {
    setLoading(true);
    try {
      await updateWebhookConfig({
        url: webhook.production?.url,
        enabledEvents: webhook.production?.enabledEvents,
        isActive: webhook.production?.isActive,
      });
      setMessage({ type: "success", text: t("webhookSaved") });
    } catch (error) {
      setMessage({ type: "error", text: t("webhookSaveFailed") });
    } finally {
      setLoading(false);
    }
  };

  const handleTestWebhook = async () => {
    setLoading(true);
    try {
      const result = await testWebhook();
      setMessage({
        type: result.status === "success" ? "success" : "error",
        text:
          result.status === "success"
            ? t("testWebhookSuccess")
            : result.error || t("testWebhookFailed"),
      });
    } catch (error) {
      setMessage({ type: "error", text: t("testWebhookFailed") });
    } finally {
      setLoading(false);
    }
  };

  const handleRotateWebhookSecret = async () => {
    if (!window.confirm(t("rotateSecretConfirm"))) return;
    setLoading(true);
    try {
      const result = await rotateWebhookSecret();
      setNewWebhookSecret(result.webhookSecret);
      setMessage({ type: "success", text: t("secretRotatedSuccess") });
    } catch (error) {
      setMessage({ type: "error", text: t("secretRotateFailed") });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: "success", text: t("copied") });
  };

  const maskPublicKey = (key) => {
    if (!key) return "";
    const len = key.length;
    const targetChars = 30;
    if (len <= targetChars) return key;
    const take = Math.floor((targetChars - 3) / 2);
    return key.substring(0, take) + "..." + key.substring(len - take);
  };

  const [profileEdit, setProfileEdit] = useState({
    companyName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
  });
  useEffect(() => {
    if (profile) {
      setProfileEdit({
        companyName: profile.companyName || "",
        contactName: profile.contactName || "",
        contactEmail: profile.contactEmail || "",
        contactPhone: profile.contactPhone || "",
      });
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await updateProfile({
        companyName: profileEdit.companyName?.trim() || undefined,
        contactName: profileEdit.contactName?.trim() || undefined,
        contactEmail: profileEdit.contactEmail?.trim() || undefined,
        contactPhone:
          profileEdit.contactPhone?.replace(/[\s-]/g, "") || undefined,
      });
      setMessage({ type: "success", text: t("profileSaved") });
      setProfile((p) => p && { ...p, ...profileEdit });
      setCompanyNameFromServer?.(profileEdit.companyName?.trim() || null);
      dispatch(companyAdminApi.util.invalidateTags(["Profile"]));
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || t("profileSaveFailed"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      setMessage({ type: "error", text: isRTL ? "נא לבחור קובץ תמונה" : "Please select an image file" });
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setLoading(true);
      try {
        await updateProfile({ branding: { ...(profile?.branding || {}), logoUrl: dataUrl } });
        setProfile((p) => p && { ...p, branding: { ...(p.branding || {}), logoUrl: dataUrl } });
        setCompanyLogoUrl?.(dataUrl);
        dispatch(companyAdminApi.util.invalidateTags(["Profile"]));
        setMessage({ type: "success", text: isRTL ? "הלוגו עודכן" : "Logo updated" });
      } catch (err) {
        setMessage({ type: "error", text: err.response?.data?.message || (isRTL ? "שגיאה בהעלאת לוגו" : "Logo upload failed") });
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveLogo = async () => {
    setLoading(true);
    try {
      await updateProfile({ branding: { ...(profile?.branding || {}), logoUrl: "" } });
      setProfile((p) => p && { ...p, branding: { ...(p.branding || {}), logoUrl: "" } });
      setCompanyLogoUrl?.(null);
      dispatch(companyAdminApi.util.invalidateTags(["Profile"]));
      setMessage({ type: "success", text: isRTL ? "הלוגו הוסר" : "Logo removed" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || (isRTL ? "שגיאה בהסרת לוגו" : "Failed to remove logo") });
    } finally {
      setLoading(false);
    }
  };

  const InfoBlock = ({ whatText, whoText }) => (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: "action.hover",
        border: "1px solid",
        borderColor: "divider",
        mb: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.5}
        sx={{ mb: 0.5 }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            display: "inline-flex",
            flexShrink: 0,
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            "& svg": { width: 22, height: 22 },
          }}
        >
          <Help fontSize="inherit" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {whatText}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          component="span"
          aria-hidden
          sx={{
            display: "inline-flex",
            color: "info.main",
            "& svg": {
              width: 22,
              height: 22,
              transform: isRTL ? "rotate(180deg)" : "none",
            },
          }}
        >
          <ArrowForward fontSize="inherit" />
        </Box>
        <Typography variant="body2" color="info.main" fontWeight={500}>
          {whoText}
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        {t("settings")}
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab label={t("profile")} />
        <Tab label={t("apiKeys")} />
        <Tab label={t("webhooks")} />
        <Tab label={t("security")} />
      </Tabs>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        TransitionProps={{ onExited: () => setMessage(null) }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {message ? (
          <Alert
            severity={message.type}
            onClose={() => setSnackbarOpen(false)}
            variant="filled"
            dir={isRTL ? "rtl" : "ltr"}
            sx={{
              width: "100%",
              alignItems: "center",
              color: "#fff",
              "& .MuiAlert-icon": { color: "#fff" },
              "& .MuiAlert-action .MuiIconButton-root": { color: "#fff" },
              "& .MuiAlert-action .MuiIconButton-root:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
              },
              ...(isRTL && {
                "& .MuiAlert-message": {
                  paddingInlineStart: 2,
                  paddingInlineEnd: 2.5,
                },
                "& .MuiAlert-action": {
                  marginInlineStart: 1,
                },
              }),
            }}
          >
            {message.text}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>

      {loading ? (
        <AppLoading fullPage />
      ) : (
        <>
          {activeTab === 0 && profile && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("companyInformation")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("personalDetailsSubtitle")}
                </Typography>
                <Stack spacing={2.5} sx={{ maxWidth: 400 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {isRTL ? "לוגו החברה" : "Company logo"}
                    </Typography>
                    {profile.branding?.logoUrl ? (
                      <Stack
                        direction="column"
                        spacing={2}
                        alignItems="center"
                        sx={{ width: "100%", gap: 1 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 88,
                            height: 88,
                            borderRadius: "50%",
                            border: "1px solid",
                            borderColor: "divider",
                            overflow: "hidden",
                            bgcolor: "background.paper",
                          }}
                        >
                          <Box
                            component="img"
                            src={profile.branding.logoUrl}
                            alt={isRTL ? "לוגו" : "Logo"}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ gap: 2}}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={handleRemoveLogo}
                            disabled={loading}
                            sx={{
                              fontWeight: 700,
                              borderWidth: 1.5,
                              "&:hover": { borderWidth: 1.5 },
                            }}
                          >
                            {isRTL ? "הסר לוגו" : "Remove logo"}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            component="label"
                            disabled={loading}
                            sx={{
                              fontWeight: 700,
                              borderWidth: 1.5,
                              "&:hover": { borderWidth: 1.5 },
                            }}
                          >
                            {isRTL ? "החלף לוגו" : "Replace logo"}
                            <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                          </Button>
                        </Stack>
                        <Divider sx={{ width: "100%", mt: 0.5, mb: 0 }} />
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          component="label"
                          disabled={loading}
                          sx={{ minWidth: 0, px: 1.5, py: 0.5, fontSize: "0.75rem" }}
                        >
                          {isRTL ? "העלה לוגו" : "Upload logo"}
                          <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          {isRTL ? "נשמר כ-BASE64 · PNG, JPG" : "Saved as BASE64 · PNG, JPG"}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                  <TextField
                    label={t("companyName")}
                    value={profileEdit.companyName}
                    onChange={(e) =>
                      setProfileEdit((p) => ({ ...p, companyName: e.target.value }))
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("contactName")}
                    value={profileEdit.contactName}
                    onChange={(e) =>
                      setProfileEdit((p) => ({
                        ...p,
                        contactName: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("contactEmail")}
                    value={profileEdit.contactEmail}
                    onChange={(e) =>
                      setProfileEdit((p) => ({
                        ...p,
                        contactEmail: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    inputProps={{ dir: "ltr" }}
                    type="email"
                  />
                  <TextField
                    label={t("phone")}
                    value={profileEdit.contactPhone}
                    onChange={(e) =>
                      setProfileEdit((p) => ({
                        ...p,
                        contactPhone: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    inputProps={{ dir: "ltr", type: "tel", inputMode: "tel" }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSaveProfile}
                    disabled={loading}
                  >
                    {loading ? t("loading") : t("saveProfile")}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {activeTab === 1 && apiKeys && (
            <Stack spacing={2}>
              <InfoBlock
                whatText={t("apiKeysWhatIs")}
                whoText={t("apiKeysWhoFor")}
              />
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t("apiKeysHowToUseTitle")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {t("apiKeysHowToUseDesc")}
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {t("apiKeysExampleHeaders")}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        width: 370,
                        maxWidth: "100%",
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                        fontSize: "0.8rem",
                        fontFamily: "monospace",
                        overflow: "auto",
                        margin: 0,
                        direction: "ltr",
                        textAlign: "left",
                      }}
                    >
                      {`X-API-Key: YOUR_PUBLIC_KEY
Content-Type: application/json`}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("apiKeysExampleBody")}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        width: 370,
                        maxWidth: "100%",
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                        fontSize: "0.8rem",
                        fontFamily: "monospace",
                        overflow: "auto",
                        margin: 0,
                        direction: "ltr",
                        textAlign: "left",
                      }}
                    >
                      {`{"templateId": "...", "clientPhone": "..."}`}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("productionApiKeys")}
                  </Typography>
                  <TextField
                    label={t("publicKey")}
                    value={maskPublicKey(apiKeys.production?.publicKey)}
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            onClick={() =>
                              copyToClipboard(apiKeys.production?.publicKey)
                            }
                          >
                            {t("copy")}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1, width: 360, maxWidth: "100%" }}
                  />
                  {newProductionSecretKey && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">
                        {t("newSecretKey")}
                      </Typography>
                      <Typography
                        component="code"
                        variant="body2"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {newProductionSecretKey}
                      </Typography>
                    </Alert>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    {t("lastRotated")}:{" "}
                    {apiKeys.production?.lastRotatedAt
                      ? new Date(
                          apiKeys.production.lastRotatedAt,
                        ).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                      : t("never")}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleRotateApiKeys("production")}
                    disabled={loading}
                    sx={{ display: "block", mt: 1 }}
                  >
                    {t("rotateKeys")}
                  </Button>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("sandboxApiKeys")}
                  </Typography>
                  <TextField
                    label={t("publicKey")}
                    value={maskPublicKey(apiKeys.sandbox?.publicKey)}
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            onClick={() =>
                              copyToClipboard(apiKeys.sandbox?.publicKey)
                            }
                          >
                            {t("copy")}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1, width: 360, maxWidth: "100%" }}
                  />
                  {newSandboxSecretKey && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">
                        {t("newSecretKey")}
                      </Typography>
                      <Typography
                        component="code"
                        variant="body2"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {newSandboxSecretKey}
                      </Typography>
                    </Alert>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleRotateApiKeys("sandbox")}
                    disabled={loading}
                    sx={{ display: "block", mt: 1 }}
                  >
                    {t("rotateSandboxKeys")}
                  </Button>
                </CardContent>
              </Card>
            </Stack>
          )}

          {activeTab === 2 && webhook && (
            <>
              <InfoBlock
                whatText={t("webhookWhatIs")}
                whoText={t("webhookWhoFor")}
              />
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("webhookSendTitle")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    {t("webhookPayloadExample")}
                  </Typography>
                  <Box
                    component="pre"
                    dir="ltr"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      fontSize: "0.75rem",
                      overflow: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      maxWidth: 370,
                      direction: "ltr",
                    }}
                  >
                    {`POST your-url
Content-Type: application/json
X-Webhook-Signature: <signature>

{
  "event": "contract.signed",
  "contractId": "...",
  "timestamp": "2024-01-15T12:00:00Z"
}`}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t("webhookExpectExample")}
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("webhookConfiguration")}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={webhook.production?.isActive || false}
                        onChange={(e) =>
                          setWebhook({
                            ...webhook,
                            production: {
                              ...webhook.production,
                              isActive: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label={t("webhookEnabled")}
                    sx={{ display: "block", mb: 1, width: "max-content" }}
                  />
                  <TextField
                    label={t("webhookUrl")}
                    value={webhook.production?.url || ""}
                    onChange={(e) =>
                      setWebhook({
                        ...webhook,
                        production: {
                          ...webhook.production,
                          url: e.target.value,
                        },
                      })
                    }
                    placeholder="https://your-server.com/webhook"
                    fullWidth
                    size="small"
                    inputProps={{ dir: "ltr" }}
                    sx={{ mb: 1 }}
                  />
                  {newWebhookSecret && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">
                        {t("newWebhookSecret")}
                      </Typography>
                      <Typography
                        component="code"
                        variant="body2"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {newWebhookSecret}
                      </Typography>
                    </Alert>
                  )}
                  <Stack direction="column" spacing={1}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRotateWebhookSecret}
                        disabled={loading}
                      >
                        {t("rotateSecret")}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleTestWebhook}
                        disabled={loading}
                        startIcon={<Science />}
                        sx={{
                          bgcolor: "#fff",
                          color: "#000",
                          "& .MuiButton-startIcon": isRTL
                            ? { marginLeft: 0.75 }
                            : { marginRight: 0.75 },
                          "&:hover": {
                            bgcolor: "grey.100",
                          },
                        }}
                      >
                        {t("sendTest")}
                      </Button>
                    </Stack>
                    <Button
                      variant="contained"
                      onClick={handleUpdateWebhook}
                      disabled={loading}
                      sx={{ mt: 1.5, width: 370, maxWidth: "100%" }}
                    >
                      {t("save")}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}

          {activeTab === 3 && security && (
            <>
              <InfoBlock
                whatText={t("ipWhitelistWhatIs")}
                whoText={t("ipWhitelistWhoFor")}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("ipWhitelistExamples")}
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("securitySettings")}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={security.ipWhitelist?.enabled || false}
                        onChange={(e) =>
                          setSecurity({
                            ...security,
                            ipWhitelist: {
                              ...security.ipWhitelist,
                              enabled: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label={t("ipWhitelistEnabled")}
                    sx={{ display: "block", mb: 2 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("allowedIpAddresses")}
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 4 }}>
                    {(security.ipWhitelist?.allowedIps || []).map(
                      (ip, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            value={ip}
                            onChange={(e) => {
                              const newIps = [
                                ...(security.ipWhitelist?.allowedIps || []),
                              ];
                              newIps[index] = e.target.value;
                              setSecurity({
                                ...security,
                                ipWhitelist: {
                                  ...security.ipWhitelist,
                                  allowedIps: newIps,
                                },
                              });
                            }}
                            placeholder="192.168.1.1 or 10.0.0.0/24"
                            sx={{ width: 350, maxWidth: "100%" }}
                            inputProps={{ dir: "ltr" }}
                          />
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => {
                              const newIps = (
                                security.ipWhitelist?.allowedIps || []
                              ).filter((_, i) => i !== index);
                              setSecurity({
                                ...security,
                                ipWhitelist: {
                                  ...security.ipWhitelist,
                                  allowedIps: newIps,
                                },
                              });
                            }}
                            sx={{
                              display: "inline-flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: 40,
                            }}
                          >
                            {t("delete")}
                          </Button>
                        </Box>
                      ),
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setSecurity({
                          ...security,
                          ipWhitelist: {
                            ...security.ipWhitelist,
                            allowedIps: [
                              ...(security.ipWhitelist?.allowedIps || []),
                              "",
                            ],
                          },
                        })
                      }
                      sx={{ alignSelf: "flex-start", minHeight: 40 }}
                    >
                      {isRTL ? "הוסף כתובת IP" : "Add IP Address"}
                    </Button>
                  </Stack>
                  <Stack spacing={2} sx={{ mt: 5 }}>
                    <TextField
                      label={t("rateLimitPerMinute")}
                      type="number"
                      value={security.rateLimit?.requestsPerMinute ?? 60}
                      onChange={(e) =>
                        setSecurity({
                          ...security,
                          rateLimit: {
                            ...security.rateLimit,
                            requestsPerMinute: parseInt(e.target.value, 10),
                          },
                        })
                      }
                      size="small"
                      sx={{ width: 200 }}
                      inputProps={{ dir: "ltr" }}
                    />
                    <Button
                      variant="contained"
                      onClick={async () => {
                        setLoading(true);
                        try {
                          const cleanedSecurity = {
                            ...security,
                            ipWhitelist: {
                              ...security.ipWhitelist,
                              allowedIps: (
                                security.ipWhitelist?.allowedIps || []
                              ).filter((ip) => ip.trim()),
                            },
                          };
                          await updateSecuritySettings(cleanedSecurity);
                          setMessage({
                            type: "success",
                            text: t("securitySaved"),
                          });
                        } catch (error) {
                          setMessage({
                            type: "error",
                            text: t("securitySaveFailed"),
                          });
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      sx={{ height: 40, alignSelf: "flex-start" }}
                    >
                      {t("saveSettings")}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default CompanySettings;
